import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  List,
  Row,
  Segmented,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  CalendarOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  PlayCircleOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import GoogleSignInButton from '../../features/auth/GoogleSignInButton';
import { AuthRole, AuthUser } from '../../features/auth/types';

const { Title, Paragraph, Text } = Typography;

type DJDepotPageProps = {
  authError: string | null;
  authRole: AuthRole;
  authStatus: 'loading' | 'anonymous' | 'authenticated' | 'error';
  authUser: AuthUser | null;
  googleClientId: string;
  onAuthRoleChange: (role: AuthRole) => void;
  onGoogleCredential: (credential: string) => void;
  onSignOut: () => void;
};

const featuredDJs = [
  {
    name: 'DJ Solstice',
    city: 'Brooklyn, NY',
    styles: ['Open Format', 'House', 'Afrobeats'],
    bookingUrl: 'mailto:bookings@djsolstice.example',
    mixType: 'audio',
    mixLabel: 'Late Night Rooftop Session',
    embedUrl: 'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/4259071&color=%23111111&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
  },
  {
    name: 'VJ Pulse',
    city: 'Miami, FL',
    styles: ['Video Mixes', 'Latin Club', 'Festival'],
    bookingUrl: 'mailto:team@vjpulse.example',
    mixType: 'video',
    mixLabel: 'Sunset Crowd Control',
    embedUrl: 'https://www.youtube.com/embed/ScMzIvxBSi4',
  },
  {
    name: 'DJ Northstar',
    city: 'Chicago, IL',
    styles: ['Corporate Events', 'Hip-Hop', 'Throwbacks'],
    bookingUrl: 'mailto:hello@northstardj.example',
    mixType: 'audio',
    mixLabel: 'Warm-Up Set for Brand Launches',
    embedUrl: 'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/68417774&color=%231677ff&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
  },
];

const clientSteps = [
  'Sign in with Google to save favorite DJs and event requirements.',
  'Share your event date, city, budget, and vibe so the right DJs can respond.',
  'Review mixes, compare booking links, and move the conversation off-platform when ready.',
];

const djSteps = [
  'Use Google sign-in for a low-friction account and profile ownership.',
  'Add an audio mix link or YouTube video mix, genres, service area, and booking link.',
  'Keep one profile that works for promoters, private events, weddings, and nightlife clients.',
];

const DJDepotPage = ({
  authError,
  authRole,
  authStatus,
  authUser,
  googleClientId,
  onAuthRoleChange,
  onGoogleCredential,
  onSignOut,
}: DJDepotPageProps) => (
  <div className="dj-depot-page">
    <section className="dj-hero-section">
      <Row gutter={[32, 32]} align="middle">
        <Col xs={24} xl={14}>
          <Title className="dj-hero-title">
            DJ Depot
          </Title>
          <Paragraph className="dj-hero-copy">
            A booking-ready marketplace where DJs showcase audio mixes, video
            sets, and direct contact info, while event organizers sign in to
            find the right sound faster.
          </Paragraph>
          <Space size="middle" wrap className="dj-hero-actions">
            <Button size="large" href="#dj-directory" icon={<CustomerServiceOutlined />}>
              Explore DJs
            </Button>
          </Space>
        </Col>
        <Col xs={24} xl={10}>
          <Card className="dj-value-card" variant="borderless">
            <Space direction="vertical" size="middle" className="dj-auth-stack">
              <div>
                <Tag color="blue">Get Started</Tag>
                <Title level={4}>Sign in with Google</Title>
              </div>

              <div>
                <Text strong>I am a:</Text>
                <div className="dj-role-toggle">
                  <Segmented
                    block
                    options={[
                      { label: 'DJ', value: 'dj' },
                      { label: 'Client', value: 'client' },
                    ]}
                    value={authRole}
                    onChange={(value) => onAuthRoleChange(value as AuthRole)}
                    disabled={Boolean(authUser)}
                  />
                </div>
              </div>

              {authUser ? (
                <Card className="dj-session-card" size="small">
                  <Space align="start" size="middle">
                    <Avatar
                      size={56}
                      src={authUser.picture}
                      icon={<UserOutlined />}
                    />
                    <Space direction="vertical" size={4}>
                      <Text strong>{authUser.name}</Text>
                      <Text type="secondary">{authUser.email}</Text>
                      <Space wrap>
                        <Tag color="green">
                          {authUser.role === 'dj' ? 'DJ account' : 'Client account'}
                        </Tag>
                        {authUser.emailVerified ? (
                          <Tag icon={<SafetyCertificateOutlined />} color="blue">
                            Verified email
                          </Tag>
                        ) : null}
                      </Space>
                    </Space>
                  </Space>
                  <Button
                    className="dj-signout-button"
                    icon={<LogoutOutlined />}
                    onClick={onSignOut}
                  >
                    Sign out
                  </Button>
                </Card>
              ) : googleClientId ? (
                <GoogleSignInButton
                  clientId={googleClientId}
                  onCredential={onGoogleCredential}
                />
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="Set REACT_APP_GOOGLE_CLIENT_ID to render the Google sign-in button."
                />
              )}

              {authStatus === 'loading' ? (
                <Text type="secondary">Checking authentication status...</Text>
              ) : null}

              {authError ? (
                <Alert type="error" showIcon message={authError} />
              ) : null}
            </Space>
          </Card>
        </Col>
      </Row>
    </section>

    <Divider className="section-divider" />

    <section id="dj-directory" className="dj-directory-section">
      <div className="section-heading">
        <Title level={2}>Featured DJ Directory</Title>
        <Paragraph type="secondary">
          These are fake DJ's, we'll have real ones on here soon.
        </Paragraph>
      </div>
      <Row gutter={[24, 24]}>
        {featuredDJs.map((dj) => (
          <Col xs={24} lg={12} xl={8} key={dj.name}>
            <Card className="dj-card" variant="borderless">
              <Space direction="vertical" size="middle" className="dj-card-stack">
                <div>
                  <Text className="dj-card-city">{dj.city}</Text>
                  <Title level={4} className="dj-card-title">
                    {dj.name}
                  </Title>
                  <Space size={[8, 8]} wrap>
                    {dj.styles.map((style) => (
                      <Tag key={style}>{style}</Tag>
                    ))}
                  </Space>
                </div>

                <div>
                  <Text strong>{dj.mixLabel}</Text>
                  <div className="dj-embed-wrapper">
                    <iframe
                      src={dj.embedUrl}
                      title={`${dj.name} ${dj.mixType} mix`}
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>

                <Space wrap>
                  <Button
                    type="primary"
                    icon={<CalendarOutlined />}
                    href={dj.bookingUrl}
                  >
                    Booking Info
                  </Button>
                  <Button icon={dj.mixType === 'video' ? <VideoCameraOutlined /> : <PlayCircleOutlined />}>
                    {dj.mixType === 'video' ? 'Video Mix' : 'Audio Mix'}
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </section>

    <Divider className="section-divider" />

    <section className="dj-paths-section">
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card className="dj-path-card dj-path-card-performer" variant="borderless">
            <Space direction="vertical" size="middle">
              <Tag color="blue">For DJs</Tag>
              <Title level={3}>Upload your mixes, and set your rates</Title>
              <Paragraph>
                Create your personalized profile and control what you want clients to see.
                Upload your mixes, link your socials, and set your rate.
              </Paragraph>
              {/* <List
                dataSource={djSteps}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              /> */}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="dj-path-card dj-path-card-client" variant="borderless">
            <Space direction="vertical" size="middle">
              <Tag color="gold">For Clients</Tag>
              <Title level={3}>Tell the platform what kind of DJ you need</Title>
              <Paragraph>
                Find the vibe and price you're looking for, faster.
                Clients can filter by price, location, genre etc. to book the
                right DJ for their venue or event.
              </Paragraph>
              {/* <List
                dataSource={clientSteps}
                renderItem={(item) => <List.Item>{item}</List.Item>}
              /> */}
            </Space>
          </Card>
        </Col>
      </Row>
    </section>
  </div>
);

export default DJDepotPage;
